import cv from "opencv-ts";
import UPNG from "upng-js";
import quantize, {RgbPixel} from "quantize";
import localforage from "localforage";
// import GIF from 'gif.js';
// import { encode as encodeWebP } from 'webp-wasm';

interface ResizeImageFileResult {
    file: File
    resized: boolean
    originalWidth?: number
    originalHeight?: number
    sourceImageUrl: string;
    resultImageUrl: string;
}
export const resizeImageFile = (
    file: File,
    maxSize: number
): Promise<ResizeImageFileResult> => {
    const reader = new FileReader()
    const image = new Image();
    const canvas = document.createElement('canvas')

    const resize = (): ResizeImageFileResult => {
        let { width, height } = image

        if (width > height) {
            if (width > maxSize) {
                height *= maxSize / width
                width = maxSize
            }
        } else if (height > maxSize) {
            width *= maxSize / height
            height = maxSize
        }


        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        if (!ctx) {
            throw new Error('could not get context')
        }
        canvas.getContext('2d')?.drawImage(image, 0, 0, width, height)
        const dataUrl = canvas.toDataURL('image/jpeg')
        const blob = dataURItoBlob(dataUrl)
        const f = new File([blob], file.name, {
            type: file.type,
        })

        let sourceImage = '';
        let resized = true;
        if (width === image.width && height === image.height) {
            resized = false;
            sourceImage = dataUrl;
        }
        return {
            file: f,
            resized: resized,
            originalWidth: image.width,
            originalHeight: image.height,
            sourceImageUrl: sourceImage,
            resultImageUrl: dataUrl,
        }
    }

    return new Promise((resolve, reject) => {
        if (!file.type.match(/image.*/)) {
            reject(new Error('Not an image'))
            return
        }
        reader.onload = (readerEvent: any) => {
            image.onload = () => resolve(resize())
            image.src = readerEvent.target.result
        }
        reader.readAsDataURL(file)
    })
}

/**
 * 判断是否是某种类型的图片
 * 常见图片文件的魔数：
 * JPEG/JPG: FF D8 FF
 * PNG: 89 50 4E 47 0D 0A 1A 0A
 * GIF: 47 49 46 38
 * BMP: 42 4D
 * @param targetFileType 图片文件类型
 * @param file 图片文件
 * @param signature 图片文件的魔数
 */
const isImageType = async (targetFileType: string, file: File, signature: Array<number>) => {
    if (!file || !file.type.endsWith(targetFileType)) return false;// 不是targetFileType类型的图片

    // // 提取前signature.length个字节
    // const arraybuffer = await file.slice(0, signature.length).arrayBuffer();
    //
    // // 转为 8位无符号整数数组 方便对比
    // // https://developer.mozilla.org/zh-CN/docs/Web/JavaScript/Reference/Global_Objects/Uint8Array
    // const source = new Uint8Array(arraybuffer);
    //
    // // 逐个字节对比
    // for (let i = 0; i < signature.length; i++) {
    //     if (source[i] !== signature[i]) {
    //         return false;
    //     }
    // }
    return true;
}

/**
 * 判断是否是png图片
 * @param file
 */
export const isPNG = async (file: File) => {
    // PNG 的前 8 个字节是（16进制表示）：89 50 4E 47 0D 0A 1A 0A
    const signature = [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A];
    // const signature = [137, 80, 78, 71, 13, 10, 26, 10];
    return await isImageType('png', file, signature);
}

/**
 * 判断是否是JPEG/JPG图片
 * @param file
 */
export const isJPEG = async (file: File) => {
    const signature = [0xFF, 0xD8, 0xFF];
    return await isImageType('jpeg', file, signature);
}

/**
 * 判断是否是WebP图片
 * @param file
 */
export const isWebp = async (file: File) => {
    const signature: number[] = [];
    return await isImageType('webp', file, signature);
}
/**
 * 判断是否是GIF图片
 * @param file
 */
export const isGIF = async (file: File) => {
    const signature = [0x47, 0x49, 0x46, 0x38];
    return await isImageType('gif', file, signature);
}

/**
 * 压缩png图像
 * @param img
 * @param originalSize
 * @param quality
 */
export const compressPNG = async (img: HTMLImageElement, originalSize: number, quality = 0.8) => {
    // 处理 Safari 尺寸限制
    const MAX_PIXELS = 16_777_216;
    const scale = (img.width * img.height > MAX_PIXELS)
        ? Math.sqrt(MAX_PIXELS / (img.width * img.height))
        : 1;

    const canvas = document.createElement('canvas');
    canvas.width = Math.floor(img.width * scale);
    canvas.height = Math.floor(img.height * scale);

    const ctx = canvas.getContext('2d', {
        willReadFrequently: true,
        alpha: true
    });
    if (!ctx) return;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);

    // 使用 UPNG.js 直接编码并量化
    const png = UPNG.encode([imgData.data.buffer], canvas.width, canvas.height, 256 * quality);
    const blob = new Blob([png], { type: 'image/png' });

    const size = blob.size;
    const url = URL.createObjectURL(blob);
    return size > originalSize ? {
        size: originalSize,
        url: canvas.toDataURL("image/png")
    } : {
        size: size,
        url: url
    };
};

/**
 * 压缩JPEG图像
 * @param img
 * @param originalSize
 * @param quality
 */
export const compressJPEG = async (img: HTMLImageElement, originalSize:number, quality = 80) => {
    // 处理 Safari 尺寸限制
    const MAX_PIXELS = 16_777_216;
    const scale = (img.width * img.height > MAX_PIXELS)
        ? Math.sqrt(MAX_PIXELS / (img.width * img.height))
        : 1;
    const canvas = document.createElement('canvas');
    canvas.width = Math.floor(img.width * scale);
    canvas.height = Math.floor(img.height * scale);
    const ctx = canvas.getContext('2d', {
        willReadFrequently: true,
        alpha: true
    });
    if (!ctx) return;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const jpegDataUrl = canvas.toDataURL('image/jpeg', quality);

    // 使用 UPNG.js 直接编码并量化
    const png = UPNG.encode([imgData.data.buffer], canvas.width, canvas.height, 256 * quality);
    const blob = new Blob([png], { type: 'image/jpeg' });

    const size = blob.size;
    const url = URL.createObjectURL(blob);
    return size > originalSize ? {
        size: originalSize,
        url: jpegDataUrl
    } : {
        size: size,
        url: url
    };
};

/**
 * 压缩webp图像
 * @param img
 * @param originalSize
 * @param quality
 */
export const compressWebP = async (img: HTMLImageElement, originalSize:number, quality = 80) => {
    try {
        // 处理 Safari 尺寸限制
        const MAX_PIXELS = 16_777_216;
        const scale = (img.width * img.height > MAX_PIXELS)
            ? Math.sqrt(MAX_PIXELS / (img.width * img.height))
            : 1;
        const canvas = document.createElement('canvas');
        canvas.width = Math.floor(img.width * scale);
        canvas.height = Math.floor(img.height * scale);
        const ctx = canvas.getContext('2d', {
            willReadFrequently: true,
            alpha: true
        });
        if (!ctx) return;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        // const webpData = await encodeWebP(imageData, {quality: quality * 100});
        const webpData = "";
        const compressedBlob = new Blob([webpData], { type: 'image/webp' });

        const compressedUrl = URL.createObjectURL(compressedBlob);
        const size = compressedBlob.size;
        return size > originalSize ? {
            size: originalSize,
            url: canvas.toDataURL("image/webp")
        } : {
            size: size,
            url: compressedUrl
        };
    } catch (error) {
        console.error('WebP encoding failed, falling back to JPEG:', error);
        // Fallback to JPEG if WebP encoding fails
        return await compressJPEG(img, originalSize);
    }
};

/**
 * 压缩GIF图像
 * @param img
 * @param ctx
 * @param canvas
 * @param originalSize
 * @param quality
 */
// export const compressGIF = async (img: HTMLImageElement, ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, originalSize: number, quality = 80) => {
//     const gif = new GIF({
//         workers: 2,
//         quality: quality, // Lower quality for smaller size
//         workerScript: '/gif.worker.js' // Ensure gif.worker.js is in public/
//     });
//
//     return await new Promise((resolve, reject) => {
//         gif.addFrame(ctx, { copy: true, delay: 200 });
//         gif.on('finished', (blob: Blob) => {
//             const compressedUrl = URL.createObjectURL(blob);
//             const size = blob.size;
//             resolve(size > originalSize ? {
//                 size: originalSize,
//                 url: canvas.toDataURL("image/gif")
//             } : {
//                 size: size,
//                 url: compressedUrl
//             });
//         });
//         gif.render();
//     })
// };

/**
 * 加载图片对象
 * @param url 图片路径
 */
export function loadImage(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image()
        img.crossOrigin = 'Anonymous'
        img.src = url
        img.onload = () => resolve(img)
        img.onerror = () => reject(new Error(`Failed to load image from ${url}`))
    })
}

/**
 * 调整image尺寸
 * @param image
 * @param targetWidth
 * @param targetHeight
 */
export async function resizeImage(image: HTMLImageElement, targetWidth: number, targetHeight: number): Promise<HTMLCanvasElement> {
    // 将图像转换为 OpenCV Mat 对象
    const src = cv.imread(image);

    // 创建目标 Mat 对象
    const dst = new cv.Mat();

    // 调整图像大小
    const dSize = new cv.Size(targetWidth, targetHeight);
    cv.resize(src, dst, dSize, 0, 0, cv.INTER_LINEAR);

    // 将结果转换为 Canvas 图像
    const canvas = document.createElement('canvas');
    cv.imshow(canvas, dst);

    // 释放内存
    src.delete();
    dst.delete();

    return canvas;
}

/**
 * 调整canvas尺寸
 * @param canvas
 * @param targetWidth
 * @param targetHeight
 */
export async function resizeCanvasImage(
    canvas: HTMLCanvasElement,
    targetWidth: number,
    targetHeight: number
): Promise<HTMLCanvasElement> {
    // 将 Canvas 图像转换为 OpenCV Mat 对象
    const src = cv.imread(canvas);

    // 创建目标 Mat 对象
    const dst = new cv.Mat();

    // 调整图像大小
    const dSize = new cv.Size(targetWidth, targetHeight);
    cv.resize(src, dst, dSize, 0, 0, cv.INTER_LINEAR);

    // 将结果转换为 Canvas 图像
    const resizedCanvas = document.createElement('canvas');
    cv.imshow(resizedCanvas, dst);

    // 释放内存
    src.delete();
    dst.delete();

    return resizedCanvas;
}

// blob转ArrayBuffer
export const blobToArrayBuffer = (blob: Blob, callback: (arrayBuffer: ArrayBuffer) => void) => {
    const reader = new FileReader();
    reader.onload = () => callback(reader.result as ArrayBuffer);
    reader.readAsArrayBuffer(blob);
}

// base64转blob
export const dataURItoBlob = (dataURI: string) => {
    const mime = dataURI.split(',')[0].split(':')[1].split(';')[0]
    const binary = atob(dataURI.split(',')[1])
    const array = []
    for (let i = 0; i < binary.length; i += 1) {
        array.push(binary.charCodeAt(i))
    }
    return new Blob([new Uint8Array(array)], { type: mime })
}

export const imageDataToDataURL = (imageData: ImageData) => {
    // 创建 canvas
    const canvas = document.createElement('canvas')
    canvas.width = imageData.width
    canvas.height = imageData.height

    // 绘制 imageData 到 canvas
    const ctx = canvas.getContext('2d') || new CanvasRenderingContext2D();
    ctx.putImageData(imageData, 0, 0)

    // 导出为数据 URL
    return canvas.toDataURL();
}

// Float32Array转base64
export const float32ArrayToBase64 = (floatArray: Float32Array, width: number, height: number) => {
    // 创建一个 Canvas 元素
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d') || new CanvasRenderingContext2D();

    // 创建 Uint8ClampedArray，长度为 width * height * 4
    const uint8ClampedArray = new Uint8ClampedArray(width * height * 4);
    // 将浮点数据归一化并填充到 Uint8ClampedArray
    for (let i = 0; i < floatArray.length; i++) {
        const value = Math.floor(floatArray[i] * 255); // 归一化到 [0, 255]
        uint8ClampedArray[i * 4] = value;     // R
        uint8ClampedArray[i * 4 + 1] = value; // G
        uint8ClampedArray[i * 4 + 2] = value; // B
        uint8ClampedArray[i * 4 + 3] = 255;   // A (完全不透明)
    }

    // 将 Uint8Array 转换为 ImageData
    const imageData = new ImageData(uint8ClampedArray, width, height);

    // 将 ImageData 绘制到 Canvas 上
    ctx.putImageData(imageData, 0, 0);

    // 将 Canvas 转换为 Base64 图片
    return canvas.toDataURL(); // 默认格式为 image/png
}

// 上传图片
export const uploadImage = async (file: File, showAlert: any, callback: (file: File) => void) => {
    if (!file || !file.type.match('image.*')) return;
    try {
        if (file.size > 10 * 1024 * 1024) {// 不能大于10MB
            throw new Error('file too large')
        }
        callback(file);
    } catch (e) {
        // eslint-disable-next-line
        showAlert({
            message: `error: image upload error`,
            type: 'error'
        }, 3000);
    }
}

export const getCachedFile = async (url: string, type: string) => {// CDN加载的数据做缓存
    const cacheKey = url.split("/").pop() + '';
    const cached = await localforage.getItem(cacheKey);
    if (cached) return new Blob([cached as Blob], { type: type });

    const response = await fetch(url);
    const blob = await response.blob();
    await localforage.setItem(cacheKey, blob);
    return blob;
};
